import { Field, SelectField, type SelectFieldOption } from '@today/ui-components'
import { Button } from '@today/ui-primitives'
import type { ReactElement } from 'react'
import { SaveRow, SettingsSection } from './settings-section'

/** Copy of {@link ProfileSettingsView}; each omitted key falls back to English. */
export interface ProfileSettingsLabels {
	title: string
	description: string
	displayName: string
	/** Shown under the name while an override is in effect; `{name}` is the IdP default. */
	idpDefault: string
	reset: string
	language: string
	timezone: string
	save: string
	saved: string
}

const DEFAULT_LABELS: ProfileSettingsLabels = {
	title: 'Profile',
	description: 'How TODAY names you, the language it speaks and your timezone.',
	displayName: 'Display name',
	idpDefault: 'Your identity provider calls you {name}.',
	reset: 'Reset to identity-provider default',
	language: 'Language',
	timezone: 'Timezone',
	save: 'Save profile',
	saved: 'Profile saved.',
}

/**
 * Props for {@link ProfileSettingsView}. Every value is the draft the app holds;
 * the options carry their own copy (the "Automatic" / "Device timezone" entries included).
 *
 * @property overridden - The saved name is the user's own; shows the IdP default and the reset action.
 * @property displayNameError - A refusal of the draft name, shown on the field.
 * @property error - A server refusal of the save.
 * @property saved - The last save succeeded.
 */
export interface ProfileSettingsViewProps {
	displayName: string
	onDisplayNameChange: (value: string) => void
	displayNameError?: string | null
	overridden: boolean
	idpDisplayName: string
	onReset: () => void
	language: string
	languageOptions: SelectFieldOption[]
	onLanguageChange: (value: string) => void
	timezone: string
	timezoneOptions: SelectFieldOption[]
	onTimezoneChange: (value: string) => void
	onSave: () => void
	isSaving?: boolean
	error?: string | null
	saved?: boolean
	labels?: Partial<ProfileSettingsLabels>
}

/** The Settings "Profile" section: display-name override, UI language and timezone (organism, TO-197). */
export function ProfileSettingsView(props: ProfileSettingsViewProps): ReactElement {
	const text = { ...DEFAULT_LABELS, ...props.labels }
	const nameHelp = props.overridden ? text.idpDefault.replace('{name}', props.idpDisplayName) : undefined
	return (
		<SettingsSection title={text.title} description={text.description} error={props.error}>
			<Field
				label={text.displayName}
				value={props.displayName}
				onChange={(event) => props.onDisplayNameChange(event.target.value)}
				aria-invalid={props.displayNameError ? true : undefined}
				description={props.displayNameError ?? nameHelp}
			/>
			{props.overridden ? (
				<div>
					<Button variant="muted" onPress={props.onReset} isDisabled={props.isSaving}>
						{text.reset}
					</Button>
				</div>
			) : null}
			<SelectField
				label={text.language}
				options={props.languageOptions}
				value={props.language}
				onValueChange={props.onLanguageChange}
			/>
			<SelectField
				label={text.timezone}
				options={props.timezoneOptions}
				value={props.timezone}
				onValueChange={props.onTimezoneChange}
			/>
			<SaveRow
				label={text.save}
				savedLabel={text.saved}
				onSave={props.onSave}
				isSaving={props.isSaving}
				saved={props.saved}
			/>
		</SettingsSection>
	)
}
